import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PUB_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = PUB_KEY ? loadStripe(PUB_KEY) : null;

const TIER_LABELS = {
  basic: { name: "Basic", color: "#A1A1AA" },
  standard: { name: "Standard", color: "#FFBE00" },
  premium: { name: "Premium", color: "#E619A8" },
};

function CheckoutForm({ amount, currency, tier, cycle, isFeatured }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    // For featured payments, after success we want to land back on the listing edit page
    // so the user can finalize the annuncio (with "Crea annuncio" / "Salva modifiche" button).
    const urlParams = new URLSearchParams(window.location.search);
    const listingId = urlParams.get("listing_id");
    const base = `${window.location.origin}/pagamento/successo`;
    const returnUrl = urlParams.get("featured") === "1" && listingId
      ? `${base}?next=${encodeURIComponent(`/dashboard/${listingId}/modifica?just_featured=1`)}`
      : base;
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });
    if (confirmError) {
      const msg = confirmError.message || "Pagamento non completato";
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const label = isFeatured
    ? { name: "Home Spotlight", color: "#E619A8" }
    : (TIER_LABELS[tier] || { name: tier, color: "#FFBE00" });
  const priceStr = Number(amount).toFixed(2).replace(".", ",");
  const subtitle = isFeatured ? "30 giorni in home page" : (cycle === "yearly" ? "Annuale" : "Mensile");

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="checkout-form">
      <div className="rounded-xl border border-zinc-800 bg-[#0A0A0A] p-5" data-testid="checkout-summary">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">{isFeatured ? "Potenziamento selezionato" : "Piano selezionato"}</div>
            <div className="font-heading font-black text-xl mt-1" style={{ color: label.color }}>
              {label.name} · {subtitle}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Totale</div>
            <div className="font-heading font-black text-3xl">€{priceStr}</div>
            <div className="text-[11px] text-zinc-500 uppercase tracking-wider">{String(currency).toUpperCase()}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-[#0A0A0A] p-5">
        <PaymentElement options={{ layout: "tabs" }} />
      </div>

      {error && (
        <div className="rounded-md border border-red-900/60 bg-red-950/30 text-red-300 text-sm p-3" data-testid="checkout-error">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-4 rounded-md bg-[#FFBE00] text-black font-bold uppercase tracking-wider text-sm disabled:opacity-60 transition-all hover:bg-[#E619A8] hover:text-white"
        data-testid="checkout-pay-btn"
      >
        {submitting ? "Elaborazione…" : `Paga €${priceStr}`}
      </button>

      <p className="text-center text-[11px] text-zinc-500">
        Pagamento sicuro gestito da <span className="text-zinc-300 font-semibold">Stripe</span>.
        I tuoi dati carta non transitano dai nostri server.
      </p>
    </form>
  );
}

export default function Checkout() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isFeatured = params.get("featured") === "1";
  const tier = params.get("tier");
  const cycle = params.get("cycle") || "monthly";
  const listingId = params.get("listing_id") || null;

  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(0);
  const [currency, setCurrency] = useState("eur");
  const [initError, setInitError] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const redirect = isFeatured
        ? `/login?redirect=/checkout?featured=1&listing_id=${listingId || ""}`
        : `/login?redirect=/checkout?tier=${tier}&cycle=${cycle}${listingId ? `&listing_id=${listingId}` : ""}`;
      nav(redirect);
      return;
    }
    if (!PUB_KEY) {
      setInitError("Stripe non configurato (publishable key mancante)");
      return;
    }
    let p;
    if (isFeatured) {
      if (!listingId) { setInitError("listing_id mancante per slot featured"); return; }
      p = api.post("/featured/create-payment-intent", { listing_id: listingId });
    } else {
      if (!tier || !["basic", "standard", "premium"].includes(tier)) {
        setInitError("Piano non valido");
        return;
      }
      const body = { tier, cycle };
      if (listingId) body.listing_id = listingId;
      p = api.post("/payments/create-payment-intent", body);
    }
    p.then(({ data }) => {
      setClientSecret(data.client_secret);
      setAmount(data.amount);
      setCurrency(data.currency);
    }).catch((e) => {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Impossibile inizializzare il pagamento";
      setInitError(msg);
      toast.error(msg);
    });
  }, [authLoading, user, tier, cycle, listingId, isFeatured, nav]);

  const options = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#FFBE00",
          colorBackground: "#0A0A0A",
          colorText: "#FAFAFA",
          colorDanger: "#E619A8",
          fontFamily: "Manrope, system-ui, sans-serif",
          borderRadius: "8px",
        },
      },
    };
  }, [clientSecret]);

  return (
    <div className="max-w-xl mx-auto px-4 md:px-8 py-16" data-testid="checkout-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E619A8] mb-2">· Checkout</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl">
          {isFeatured ? (<>Metti in <span className="text-[#FFBE00]">home page</span>.</>) : (<>Conferma <span className="text-[#FFBE00]">pagamento</span>.</>)}
        </h1>
        <p className="text-zinc-400 mt-2 text-sm">
          <Link to={isFeatured ? "/dashboard" : "/prezzi"} className="underline hover:text-[#FFBE00]">
            ← {isFeatured ? "Torna alla dashboard" : "Torna ai prezzi"}
          </Link>
        </p>
      </div>

      {initError && (
        <div className="rounded-xl border border-red-900/60 bg-red-950/30 text-red-300 p-5" data-testid="checkout-init-error">
          <div className="font-bold mb-1">Errore</div>
          <div className="text-sm">{initError}</div>
          <Link to="/prezzi" className="inline-block mt-4 px-4 py-2 rounded border border-zinc-700 text-xs uppercase tracking-wider">Torna ai prezzi</Link>
        </div>
      )}

      {!initError && !clientSecret && (
        <div className="text-center py-16" data-testid="checkout-loading">
          <i className="fas fa-circle-notch fa-spin text-3xl text-[#FFBE00]"></i>
          <p className="text-zinc-400 mt-4 text-sm">Inizializzazione pagamento…</p>
        </div>
      )}

      {!initError && clientSecret && stripePromise && options && (
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm amount={amount} currency={currency} tier={tier} cycle={cycle} isFeatured={isFeatured} />
        </Elements>
      )}
    </div>
  );
}
